import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Ban, RotateCcw, ShieldCheck, AlertTriangle } from "lucide-react";

const ROLES = ["client", "independent", "agency", "moderator", "admin"];
const STATUS_TONE = {
  active:    "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  suspended: "bg-rose-500/10    text-rose-300    border-rose-500/30",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setError({ status, detail: typeof detail === "string" ? detail : String(detail || e.message || "Failed to load users") });
      // eslint-disable-next-line no-console
      console.warn("[AdminUsers] load:", status, detail);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter(u => (u.email || "").toLowerCase().includes(s) || (u.role || "").toLowerCase().includes(s));
  }, [users, q]);

  const setRole = async (uid, role) => {
    setBusyId(uid);
    try {
      await api.post(`/admin/users/${uid}/role`, { role });
      toast.success(`Role changed to ${role}`);
      setUsers((us) => us.map(u => u.id === uid ? { ...u, role } : u));
    } catch (e) {
      console.warn("[AdminUsers] setRole:", e);
      toast.error("Role update failed");
    } finally { setBusyId(null); }
  };

  const setStatus = async (uid, status) => {
    setBusyId(uid);
    try {
      await api.post(`/admin/users/${uid}/status`, { status });
      toast.success(status === "active" ? "User restored" : "User suspended");
      setUsers((us) => us.map(u => u.id === uid ? { ...u, status } : u));
    } catch (e) {
      console.warn("[AdminUsers] setStatus:", e);
      toast.error("Status update failed");
    } finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-amber-400">Access</p>
          <h1 className="text-2xl sm:text-3xl font-serif text-stone-50 mt-1">Users</h1>
          <p className="text-sm text-stone-400 mt-1">{filtered.length} of {users.length}</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-stone-500 absolute left-3 top-3" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search email / role"
            className="pl-9 pr-3 h-10 rounded-lg bg-stone-900/60 border border-stone-700 text-sm text-stone-100 w-full sm:w-72"
            data-testid="adm-users-search"
          />
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-lg border border-rose-500/40 bg-rose-500/5 text-xs text-rose-200 flex items-start gap-2" data-testid="adm-users-error">
          <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
          <div>
            <p className="font-medium">Could not load users (HTTP {error.status ?? "?"})</p>
            <p className="text-rose-300/80 mt-0.5">{error.detail}</p>
          </div>
        </div>
      )}
      {loading && (<p className="mt-4 text-sm text-stone-400">Loading…</p>)}

      <div className="mt-5 rounded-xl border border-stone-800 bg-stone-950/50 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="adm-users-table">
          <thead className="text-[11px] uppercase tracking-wider text-stone-400 bg-stone-900/40">
            <tr>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Profiles</th>
              <th className="px-4 py-3 text-left">Joined</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-800/70">
            {filtered.map(u => (
              <tr key={u.id} data-testid={`adm-user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <p className="text-stone-100 flex items-center gap-1.5">
                    {u.role === "admin" || u.role === "super_admin" ? <ShieldCheck className="w-3.5 h-3.5 text-amber-400 flex-none" /> : null}
                    {u.email}
                  </p>
                  <p className="text-[10px] text-stone-500">{u.id?.slice(0, 8)}…</p>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={u.role}
                    onChange={(e) => setRole(u.id, e.target.value)}
                    disabled={busyId === u.id}
                    className="bg-stone-900 border border-stone-700 rounded px-2 py-1 text-xs text-stone-100"
                    data-testid={`adm-user-role-${u.id}`}
                  >
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] ${STATUS_TONE[u.status] || STATUS_TONE.active}`}>{u.status || "active"}</span>
                </td>
                <td className="px-4 py-3 text-stone-300">{u.profile_count ?? 0}</td>
                <td className="px-4 py-3 text-[11px] text-stone-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-right">
                  {u.status === "suspended" ? (
                    <button onClick={() => setStatus(u.id, "active")} disabled={busyId === u.id} className="px-2 py-1 rounded border border-emerald-500/30 text-emerald-300 text-xs inline-flex items-center gap-1" data-testid={`adm-user-restore-${u.id}`}>
                      <RotateCcw className="w-3 h-3" /> Restore
                    </button>
                  ) : (
                    <button onClick={() => setStatus(u.id, "suspended")} disabled={busyId === u.id} className="px-2 py-1 rounded border border-rose-500/30 text-rose-300 text-xs inline-flex items-center gap-1" data-testid={`adm-user-suspend-${u.id}`}>
                      <Ban className="w-3 h-3" /> Suspend
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-stone-500 text-sm">No users match the filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
